/* ============================================
   Portal Cotizaciones — Client Quotation Center
   KPIs, chip filters, expandable table, preview
   modal, PDF export, accept/reject actions.
   ============================================ */

import React, { useState, useEffect, useRef, useMemo } from 'react'
import { usePortalCliente } from '@/hooks/usePortalCliente'
import { useCachedData } from '@/hooks/useCachedData'
import { supabase } from '@/lib/supabase'
import { formatCurrency, formatDate, fmtPct, processContent, sanitizeHtml } from '@/lib/utils'
import { showToast } from '@/components/layout/Toast'
import type {
  Cotizacion,
  CotizacionItemsEstandar,
  CotizacionItemsEquipo,
  CotizacionItemsTareas,
  Empresa,
} from '@/lib/types'
import { Breadcrumbs } from '@/components/ui'
import { useCrumbInicio } from '@/lib/portal-base'
import { descargarPdf } from '@/lib/documentos'

const API_URL = import.meta.env.VITE_API_URL || 'https://api.1conecta.com'

// ─── Estado config ───
const estadoConfig: Record<string, { label: string; className: string; color: string }> = {
  enviada:        { label: 'Pendiente',       className: 'badge-enviada',    color: '#0984e3' },
  aceptada:       { label: 'Aceptada',        className: 'badge-aceptada',   color: '#00b894' },
  rechazada:      { label: 'Rechazada',       className: 'badge-rechazada',  color: '#d63031' },
  en_negociacion: { label: 'En Negociación',  className: 'badge-warning',    color: '#fdcb6e' },
}

const tipoLabel: Record<string, string> = {
  estandar: 'Estándar',
  equipo_dedicado: 'Equipo',
  tareas: 'Tareas',
}

// ─── Inline SVG Icons ───
function IconDoc() {
  return <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" /><polyline points="14 2 14 8 20 8" /><line x1="16" y1="13" x2="8" y2="13" /><line x1="16" y1="17" x2="8" y2="17" /></svg>
}
function IconClock() {
  return <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10" /><path d="M12 6v6l4 2" /></svg>
}
function IconCheck() {
  return <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6L9 17l-5-5" /></svg>
}
function IconDollar() {
  return <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="1" x2="12" y2="23" /><path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" /></svg>
}

// ─── Helpers ───

/** Detect cotizacion type from items field */
function getCotType(cot: Cotizacion): 'estandar' | 'equipo_dedicado' | 'tareas' {
  if (cot.tipo_cotizacion) return cot.tipo_cotizacion
  if (cot.items && !Array.isArray(cot.items)) {
    const obj = cot.items as unknown as Record<string, unknown>
    if (obj.tipo_cotizacion === 'equipo_dedicado') return 'equipo_dedicado'
    if (obj.tipo_cotizacion === 'tareas') return 'tareas'
  }
  return 'estandar'
}

/** Compute expiry date from emission + validez_dias */
function getExpiryDate(cot: Cotizacion): Date | null {
  if (!cot.fecha_emision || !cot.validez_dias) return null
  const d = new Date(cot.fecha_emision)
  d.setDate(d.getDate() + cot.validez_dias)
  return d
}

function isExpired(cot: Cotizacion): boolean {
  const exp = getExpiryDate(cot)
  return exp ? exp < new Date() : false
}

// ─── Component ───

export default function PortalCotizaciones() {
  const { activeClienteId: clienteId } = usePortalCliente()
  const inicio = useCrumbInicio()
  const [filterEstado, setFilterEstado] = useState<string>('')
  const [expandedId, setExpandedId] = useState<number | null>(null)
  const [previewCot, setPreviewCot] = useState<Cotizacion | null>(null)
  const [exporting, setExporting] = useState(false)
  const previewRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const m = (await import('mermaid')).default
        if (!cancelled) m.initialize({ startOnLoad: false, theme: 'base', themeVariables: { primaryColor: '#4F7FE8', primaryBorderColor: '#1C4587', lineColor: '#1C4587', secondaryColor: '#eef1f8', tertiaryColor: '#f8f9fc' } })
      } catch (e) { console.warn('[mermaid] init error:', e) }
    })()
    return () => { cancelled = true }
  }, [])

  // Run mermaid after preview modal opens with secciones
  useEffect(() => {
    if (!previewCot) return
    let cancelled = false
    const timer = setTimeout(async () => {
      try {
        const els = document.querySelectorAll('.cot-mermaid-container .mermaid')
        if (!els || els.length === 0 || cancelled) return
        console.warn('[mermaid] rendering', els.length, 'diagrams')
        const m = (await import('mermaid')).default
        if (!cancelled) await m.run({ nodes: Array.from(els) as any })
        console.warn('[mermaid] render complete')
      } catch (e) { console.warn('[mermaid] render error:', e) }
    }, 200)
    return () => { cancelled = true; clearTimeout(timer) }
  }, [previewCot])

  // Las cotizaciones del cliente, en cache: volver a la pestana no vuelve a
  // pedirlas. `clienteId` va en la clave porque el multi-cuenta cambia de
  // empresa sin recargar la app.
  const { data: cotizaciones = [], isLoading: loading, actualizar } = useCachedData(
    ['portal-cotizaciones', clienteId],
    async () => {
      const { data } = await supabase
        .from('cotizaciones')
        .select('*')
        .eq('cliente_id', clienteId)
        .neq('estado', 'borrador')
        .order('fecha_emision', { ascending: false })
      return (data ?? []) as unknown as Cotizacion[]
    },
    { activo: !!clienteId },
  )

  // El emisor para el preview, con la MISMA clave que usa Facturas: es un
  // singleton, asi que la segunda pantalla que lo pida ya lo encuentra traido.
  // Stopgap: solo columnas publicas (branding/facturacion); nunca secretos
  // (google_tokens, canales_config, firma_email_html). Fix real: RLS/vista en DB.
  const { data: empresa = null } = useCachedData(['portal-empresa'], async () => {
    const { data } = await supabase.from('empresa')
      .select('id, nombre, razon_social, ruc, telefono, email, website, ubicacion, logo_url, color_primario, color_secundario, banco, cuenta_corriente, cci, swift, direccion_banco')
      .limit(1).single()
    return (data ?? null) as unknown as Empresa | null
  })

  // ─── Metrics ───
  const metrics = useMemo(() => {
    const pendientes = cotizaciones.filter(c => c.estado === 'enviada')
    const aceptadas = cotizaciones.filter(c => c.estado === 'aceptada')
    return {
      total: cotizaciones.length,
      pendientes: pendientes.length,
      pendientesTotal: pendientes.reduce((s, c) => s + c.total, 0),
      aceptadas: aceptadas.length,
      valorTotal: cotizaciones.reduce((s, c) => s + c.total, 0),
    }
  }, [cotizaciones])

  // ─── Filtered list ───
  const filtered = filterEstado
    ? cotizaciones.filter(c => c.estado === filterEstado)
    : cotizaciones

  /*
    ─── Descargar el documento ───

    Es el mismo PDF que arma el backend para el enlace público, el que viaja
    por correo. Antes esto fotografiaba la vista previa con html2canvas y la
    pegaba en un jsPDF: salía como imagen, y a veces con el logo y las
    tarjetas de cifras en blanco. Ver `src/lib/documentos.ts`.
  */
  async function exportPDF() {
    if (!previewCot) return
    if (!previewCot.public_token) {
      showToast('Esta cotización todavía no tiene documento. Avísanos y lo generamos.', 'error')
      return
    }
    setExporting(true)
    try {
      await descargarPdf(
        `${API_URL}/api/pdf/public/cotizacion/${previewCot.public_token}/archivo`,
        `Cotizacion-${previewCot.numero}`,
      )
      showToast('PDF descargado', 'success')
    } catch { showToast('No se pudo descargar el documento', 'error') }
    finally { setExporting(false) }
  }

  // ─── Accept / Reject ───
  async function handleAction(cotId: number, nuevoEstado: 'aceptada' | 'rechazada') {
    const label = nuevoEstado === 'aceptada' ? 'aceptar' : 'rechazar'
    if (!confirm(`¿Desea ${label} esta cotización?`)) return
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const { error } = await (supabase.from('cotizaciones') as any)
        .update({ estado: nuevoEstado })
        .eq('id', cotId)
      if (error) throw error
      actualizar(prev => prev.map(c => c.id === cotId ? { ...c, estado: nuevoEstado } : c))
      showToast(`Cotización ${nuevoEstado === 'aceptada' ? 'aceptada' : 'rechazada'} exitosamente`, 'success')
    } catch {
      showToast(`Error al ${label} cotización`, 'error')
    }
  }

  // ─── Render items based on type ───
  function renderItemsSummary(cot: Cotizacion) {
    const tipo = getCotType(cot)

    if (tipo === 'estandar') {
      const items = cot.items as CotizacionItemsEstandar[]
      if (!items || !Array.isArray(items) || items.length === 0) return <div className="pd-empty-mini">Sin ítems</div>
      return (
        <table style={{ width: '100%', fontSize: '0.78rem', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid var(--border)' }}>
              <th style={{ padding: '6px 0', textAlign: 'left', fontWeight: 600, color: 'var(--text-secondary)' }}>Descripción</th>
              <th style={{ padding: '6px 0', textAlign: 'center', fontWeight: 600, color: 'var(--text-secondary)', width: 50 }}>Cant.</th>
              <th style={{ padding: '6px 0', textAlign: 'right', fontWeight: 600, color: 'var(--text-secondary)', width: 90 }}>P. Unit.</th>
              <th style={{ padding: '6px 0', textAlign: 'right', fontWeight: 600, color: 'var(--text-secondary)', width: 90 }}>Total</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, idx) => (
              <tr key={idx} style={{ borderBottom: '1px solid var(--border)' }}>
                <td style={{ padding: '6px 0' }}>{item.descripcion}</td>
                <td style={{ padding: '6px 0', textAlign: 'center' }}>{item.cantidad}</td>
                <td style={{ padding: '6px 0', textAlign: 'right' }}>{formatCurrency(item.precio_unitario, cot.moneda)}</td>
                <td style={{ padding: '6px 0', textAlign: 'right', fontWeight: 600 }}>{formatCurrency(item.total, cot.moneda)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )
    }

    if (tipo === 'equipo_dedicado') {
      const data = cot.items as CotizacionItemsEquipo
      if (!data?.equipo) return <div className="pd-empty-mini">Sin equipo</div>
      return (
        <table style={{ width: '100%', fontSize: '0.78rem', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid var(--border)' }}>
              <th style={{ padding: '6px 0', textAlign: 'left', fontWeight: 600, color: 'var(--text-secondary)' }}>Rol</th>
              <th style={{ padding: '6px 0', textAlign: 'center', fontWeight: 600, color: 'var(--text-secondary)', width: 80 }}>Seniority</th>
              <th style={{ padding: '6px 0', textAlign: 'center', fontWeight: 600, color: 'var(--text-secondary)', width: 40 }}>Qty</th>
              <th style={{ padding: '6px 0', textAlign: 'center', fontWeight: 600, color: 'var(--text-secondary)', width: 50 }}>FTE</th>
              <th style={{ padding: '6px 0', textAlign: 'right', fontWeight: 600, color: 'var(--text-secondary)', width: 60 }}>Horas</th>
              <th style={{ padding: '6px 0', textAlign: 'right', fontWeight: 600, color: 'var(--text-secondary)', width: 90 }}>Precio/h</th>
            </tr>
          </thead>
          <tbody>
            {data.equipo.map((m, idx) => (
              <tr key={idx} style={{ borderBottom: '1px solid var(--border)' }}>
                <td style={{ padding: '6px 0' }}>{m.rol_comercial}</td>
                <td style={{ padding: '6px 0', textAlign: 'center' }}>{m.seniority}</td>
                <td style={{ padding: '6px 0', textAlign: 'center' }}>{m.cantidad}</td>
                <td style={{ padding: '6px 0', textAlign: 'center' }}>{(m.dedicacion_fte * 100).toFixed(0)}%</td>
                <td style={{ padding: '6px 0', textAlign: 'right' }}>{m.horas_mensuales_linea}h</td>
                <td style={{ padding: '6px 0', textAlign: 'right' }}>{formatCurrency(m.precio_venta_hora, cot.moneda)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )
    }

    if (tipo === 'tareas') {
      const data = cot.items as CotizacionItemsTareas
      if (!data?.tareas) return <div className="pd-empty-mini">Sin tareas</div>
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {data.tareas.map((t, idx) => (
            <div key={idx} style={{ padding: '10px 14px', background: 'var(--border)', borderRadius: 8, border: '1px solid var(--border)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                <div>
                  <div style={{ fontWeight: 600, fontSize: '0.82rem' }}>{t.titulo}</div>
                  {t.descripcion_corta && (
                    <div style={{ fontSize: '0.72rem', color: 'var(--text-tertiary)', marginTop: 3 }}>{t.descripcion_corta}</div>
                  )}
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <div style={{ fontWeight: 700, fontSize: '0.85rem' }}>{formatCurrency(t.monto, cot.moneda)}</div>
                  {t.horas_estimadas && (
                    <div style={{ fontSize: '0.68rem', color: 'var(--text-tertiary)' }}>{t.horas_estimadas}h est.</div>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )
    }
    return null
  }

  // ─── Render preview items for PDF modal ───
  function renderPreviewItems(cot: Cotizacion) {
    const tipo = getCotType(cot)

    if (tipo === 'estandar') {
      const items = cot.items as CotizacionItemsEstandar[]
      if (!items || !Array.isArray(items)) return null
      return (
        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 24 }}>
          <thead>
            <tr style={{ background: '#3468a8', color: '#fff' }}>
              <th style={{ padding: '10px 12px', textAlign: 'left', fontSize: 13, fontWeight: 600 }}>Descripción</th>
              <th style={{ padding: '10px 12px', textAlign: 'center', fontSize: 13, fontWeight: 600 }}>Cant.</th>
              <th style={{ padding: '10px 12px', textAlign: 'right', fontSize: 13, fontWeight: 600 }}>P. Unit.</th>
              <th style={{ padding: '10px 12px', textAlign: 'right', fontSize: 13, fontWeight: 600 }}>Total</th>
            </tr>
          </thead>
          <tbody>
            {items.map((item, idx) => (
              <tr key={idx} style={{ borderBottom: '1px solid #e9ecef', background: idx % 2 === 1 ? '#f8fafc' : '#fff' }}>
                <td style={{ padding: '8px 12px', fontSize: 13 }}>{item.descripcion}</td>
                <td style={{ padding: '8px 12px', fontSize: 13, textAlign: 'center' }}>{item.cantidad}</td>
                <td style={{ padding: '8px 12px', fontSize: 13, textAlign: 'right' }}>{formatCurrency(item.precio_unitario, cot.moneda)}</td>
                <td style={{ padding: '8px 12px', fontSize: 13, textAlign: 'right', fontWeight: 600 }}>{formatCurrency(item.total, cot.moneda)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )
    }

    if (tipo === 'equipo_dedicado') {
      const data = cot.items as CotizacionItemsEquipo
      if (!data?.equipo) return null
      return (
        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 24 }}>
          <thead>
            <tr style={{ background: '#3468a8', color: '#fff' }}>
              <th style={{ padding: '10px 12px', textAlign: 'left', fontSize: 13, fontWeight: 600 }}>Rol</th>
              <th style={{ padding: '10px 12px', textAlign: 'center', fontSize: 13, fontWeight: 600 }}>Seniority</th>
              <th style={{ padding: '10px 12px', textAlign: 'center', fontSize: 13, fontWeight: 600 }}>Qty</th>
              <th style={{ padding: '10px 12px', textAlign: 'center', fontSize: 13, fontWeight: 600 }}>FTE</th>
              <th style={{ padding: '10px 12px', textAlign: 'right', fontSize: 13, fontWeight: 600 }}>Horas/mes</th>
              <th style={{ padding: '10px 12px', textAlign: 'right', fontSize: 13, fontWeight: 600 }}>Precio/h</th>
            </tr>
          </thead>
          <tbody>
            {data.equipo.map((m, idx) => (
              <tr key={idx} style={{ borderBottom: '1px solid #e9ecef', background: idx % 2 === 1 ? '#f8fafc' : '#fff' }}>
                <td style={{ padding: '8px 12px', fontSize: 13 }}>{m.rol_comercial}</td>
                <td style={{ padding: '8px 12px', fontSize: 13, textAlign: 'center' }}>{m.seniority}</td>
                <td style={{ padding: '8px 12px', fontSize: 13, textAlign: 'center' }}>{m.cantidad}</td>
                <td style={{ padding: '8px 12px', fontSize: 13, textAlign: 'center' }}>{(m.dedicacion_fte * 100).toFixed(0)}%</td>
                <td style={{ padding: '8px 12px', fontSize: 13, textAlign: 'right' }}>{m.horas_mensuales_linea}h</td>
                <td style={{ padding: '8px 12px', fontSize: 13, textAlign: 'right' }}>{formatCurrency(m.precio_venta_hora, cot.moneda)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )
    }

    if (tipo === 'tareas') {
      const data = cot.items as CotizacionItemsTareas
      if (!data?.tareas) return null
      return (
        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: 24 }}>
          <thead>
            <tr style={{ background: '#3468a8', color: '#fff' }}>
              <th style={{ padding: '10px 12px', textAlign: 'left', fontSize: 13, fontWeight: 600 }}>Tarea</th>
              <th style={{ padding: '10px 12px', textAlign: 'right', fontSize: 13, fontWeight: 600 }}>Horas</th>
              <th style={{ padding: '10px 12px', textAlign: 'right', fontSize: 13, fontWeight: 600 }}>Monto</th>
            </tr>
          </thead>
          <tbody>
            {data.tareas.map((t, idx) => (
              <tr key={idx} style={{ borderBottom: '1px solid #e9ecef', background: idx % 2 === 1 ? '#f8fafc' : '#fff' }}>
                <td style={{ padding: '8px 12px', fontSize: 13 }}>
                  <div style={{ fontWeight: 600 }}>{t.titulo}</div>
                  {t.descripcion_corta && <div style={{ fontSize: 11, color: '#666', marginTop: 2 }}>{t.descripcion_corta}</div>}
                </td>
                <td style={{ padding: '8px 12px', fontSize: 13, textAlign: 'right' }}>{t.horas_estimadas ? `${t.horas_estimadas}h` : '—'}</td>
                <td style={{ padding: '8px 12px', fontSize: 13, textAlign: 'right', fontWeight: 600 }}>{formatCurrency(t.monto, cot.moneda)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )
    }
    return null
  }

  // ─── Loading ───
  if (loading) {
    return (
      <div className="portal-loading-state">
        <div className="auth-spinner" style={{ margin: '0 auto 12px' }} />
        <p>Cargando cotizaciones...</p>
      </div>
    )
  }

  return (
    <div className="pc">
      <Breadcrumbs items={[inicio, { label: 'Cotizaciones' }]} />

      {/* ═══ KPI Cards ═══ */}
      <div className="pd-kpis" style={{ marginBottom: 20 }}>
        <div className="pd-kpi">
          <div className="pd-kpi-icon"><IconDoc /></div>
          <div className="pd-kpi-data">
            <span className="pd-kpi-value">{metrics.total}</span>
            <span className="pd-kpi-label">Total Cotizaciones</span>
          </div>
        </div>
        <div className={`pd-kpi ${metrics.pendientes > 0 ? 'pd-kpi--warning' : ''}`}>
          <div className="pd-kpi-icon" style={{ background: 'rgba(253,203,110,0.15)', color: '#fdcb6e' }}><IconClock /></div>
          <div className="pd-kpi-data">
            <span className="pd-kpi-value" style={{ color: metrics.pendientes > 0 ? '#fdcb6e' : 'var(--text-secondary)' }}>{metrics.pendientes}</span>
            <span className="pd-kpi-label">Pendientes</span>
          </div>
        </div>
        <div className="pd-kpi pd-kpi--success">
          <div className="pd-kpi-icon"><IconCheck /></div>
          <div className="pd-kpi-data">
            <span className="pd-kpi-value">{metrics.aceptadas}</span>
            <span className="pd-kpi-label">Aceptadas</span>
          </div>
        </div>
        <div className="pd-kpi pd-kpi--info">
          <div className="pd-kpi-icon"><IconDollar /></div>
          <div className="pd-kpi-data">
            <span className="pd-kpi-value">{formatCurrency(metrics.valorTotal)}</span>
            <span className="pd-kpi-label">Valor Total</span>
          </div>
        </div>
      </div>

      {/* ═══ Chip Filters ═══ */}
      <div className="pt-filter-chips" style={{ marginBottom: 16 }}>
        {[
          { value: '', label: 'Todas' },
          { value: 'enviada', label: 'Pendientes' },
          { value: 'aceptada', label: 'Aceptadas' },
          { value: 'rechazada', label: 'Rechazadas' },
          { value: 'en_negociacion', label: 'En Negociación' },
        ].map(opt => (
          <button
            key={opt.value}
            className={`pt-filter-chip ${filterEstado === opt.value ? 'active' : ''}`}
            onClick={() => setFilterEstado(opt.value)}
          >
            {opt.value && <span className="pt-filter-chip-dot" style={{ background: estadoConfig[opt.value]?.color || '#636e72' }} />}
            {opt.label}
          </button>
        ))}
        <span style={{ color: 'var(--text-tertiary)', fontSize: '0.78rem', marginLeft: 8 }}>
          {filtered.length} cotizaci{filtered.length !== 1 ? 'ones' : 'ón'}
        </span>
      </div>

      {/* ═══ Table ═══ */}
      {filtered.length === 0 ? (
        <div className="pd-card" style={{ textAlign: 'center', padding: '3rem' }}>
          <IconDoc />
          <h3 style={{ marginTop: 12, color: 'var(--text-secondary)' }}>Sin cotizaciones</h3>
          <p style={{ color: 'var(--text-tertiary)' }}>No hay cotizaciones registradas para tu cuenta.</p>
        </div>
      ) : (
        <div className="pf-table-wrap">
          <table className="pf-table">
            <thead>
              <tr>
                <th>Estado</th>
                <th>N°</th>
                <th>Proyecto</th>
                <th>Fecha</th>
                <th style={{ textAlign: 'right' }}>Monto</th>
                <th style={{ textAlign: 'center' }}>Tipo</th>
                <th style={{ textAlign: 'center', width: 40 }}></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(c => {
                const cfg = estadoConfig[c.estado] ?? estadoConfig.enviada
                const tipo = getCotType(c)
                const expired = isExpired(c) && c.estado === 'enviada'
                const isExpanded = expandedId === c.id

                return (
                  <React.Fragment key={c.id}>
                    <tr
                      className={`pf-table-row ${expired ? 'pf-row-overdue' : ''} ${isExpanded ? 'pf-row-expanded' : ''}`}
                      onClick={() => setExpandedId(isExpanded ? null : c.id)}
                    >
                      <td>
                        <span className={`badge ${cfg.className}`}>{cfg.label}</span>
                        {expired && <span className="pf-badge-vencida">Expirada</span>}
                      </td>
                      <td style={{ fontFamily: 'monospace', fontSize: '0.78rem' }}>{c.numero}</td>
                      <td className="pf-cell-project">{c.proyecto_nombre || '—'}</td>
                      <td>{c.fecha_emision ? formatDate(c.fecha_emision) : '—'}</td>
                      <td style={{ textAlign: 'right' }}>
                        <strong>{formatCurrency(c.total, c.moneda)}</strong>
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <span style={{
                          fontSize: '0.65rem', padding: '2px 8px', borderRadius: 4, fontWeight: 600,
                          background: tipo === 'estandar' ? 'rgba(9,132,227,0.12)' : tipo === 'equipo_dedicado' ? 'rgba(108,92,231,0.12)' : 'rgba(0,210,211,0.12)',
                          color: tipo === 'estandar' ? '#74b9ff' : tipo === 'equipo_dedicado' ? '#a29bfe' : '#00d2d3',
                        }}>{tipoLabel[tipo]}</span>
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ transition: 'transform 0.2s', transform: isExpanded ? 'rotate(180deg)' : 'rotate(0)' }}>
                          <polyline points="6 9 12 15 18 9"/>
                        </svg>
                      </td>
                    </tr>
                    {isExpanded && (
                      <tr className="pf-accordion-row">
                        <td colSpan={7}>
                          <div className="pf-accordion-cell open">
                            <div className="pf-accordion-inner">
                              {/* Items section */}
                              <div className="pf-acc-section">
                                <div className="pf-acc-section-title">Detalle de Cotización</div>
                                {renderItemsSummary(c)}
                              </div>

                              {/* Financial breakdown */}
                              <div className="pf-acc-section">
                                <div className="pf-acc-section-title">Resumen Financiero</div>
                                <div className="pf-acc-field">
                                  <span>Subtotal</span>
                                  <span>{formatCurrency(c.subtotal, c.moneda)}</span>
                                </div>
                                {c.descuento_monto > 0 && (
                                  <div className="pf-acc-field">
                                    <span>Descuento ({fmtPct(c.descuento_porcentaje)}%)</span>
                                    <span style={{ color: '#00b894' }}>−{formatCurrency(c.descuento_monto, c.moneda)}</span>
                                  </div>
                                )}
                                <div className="pf-acc-field">
                                  <span>IGV ({c.igv_porcentaje}%)</span>
                                  <span>{formatCurrency(c.igv_monto, c.moneda)}</span>
                                </div>
                                <div className="pf-acc-field pf-acc-total">
                                  <span>Total</span>
                                  <span>{formatCurrency(c.total, c.moneda)}</span>
                                </div>
                              </div>

                              {/* Validity */}
                              {c.validez_dias > 0 && (
                                <div className="pf-acc-section" style={{ fontSize: '0.75rem', color: expired ? '#d63031' : 'var(--text-tertiary)' }}>
                                  Válida hasta: {getExpiryDate(c) ? formatDate(getExpiryDate(c)!.toISOString().slice(0, 10)) : '—'}
                                  {expired && ' (expirada)'}
                                </div>
                              )}

                              {/* Secciones HTML */}
                              {c.secciones && c.secciones.length > 0 && (
                                <div className="pf-acc-section">
                                  <div className="pf-acc-section-title">Información Adicional</div>
                                  {c.secciones.map((sec, idx) => (
                                    <div key={idx} style={{ marginBottom: 12 }}>
                                      <div style={{ fontWeight: 600, fontSize: '0.78rem', marginBottom: 4, color: 'var(--text-secondary)' }}>{sec.titulo}</div>
                                      <div
                                        style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', lineHeight: 1.5 }}
                                        dangerouslySetInnerHTML={{ __html: sanitizeHtml(processContent(sec.contenido)) }}
                                      />
                                    </div>
                                  ))}
                                </div>
                              )}

                              {/* Actions */}
                              <div className="pf-acc-actions">
                                <button
                                  className="pf-acc-btn pf-acc-btn--preview"
                                  onClick={e => { e.stopPropagation(); setPreviewCot(c) }}
                                >
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
                                  Ver Detalle Completo
                                </button>
                                {c.estado === 'enviada' && (
                                  <>
                                    <button
                                      className="pf-acc-btn pf-acc-btn--confirm"
                                      onClick={e => { e.stopPropagation(); handleAction(c.id, 'aceptada') }}
                                    >
                                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 6L9 17l-5-5"/></svg>
                                      Aceptar
                                    </button>
                                    <button
                                      className="pf-acc-btn"
                                      onClick={e => { e.stopPropagation(); handleAction(c.id, 'rechazada') }}
                                      style={{ background: 'rgba(214,48,49,0.1)', color: '#d63031', border: '1px solid rgba(214,48,49,0.25)' }}
                                    >
                                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                                      Rechazar
                                    </button>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* ═══ Preview Modal ═══ */}
      {previewCot && (
        <>
          <div className="pt-overlay" style={{ zIndex: 200 }} onClick={() => setPreviewCot(null)} />
          <div className="pf-preview-center" style={{
            position: 'fixed', inset: 0, zIndex: 201, display: 'flex', alignItems: 'center', justifyContent: 'center',
            padding: '20px', pointerEvents: 'none',
          }}>
            <div className="pf-preview-container" style={{ background: 'var(--bg-card)', borderRadius: 12, maxWidth: 860, width: '100%', maxHeight: '95vh', overflow: 'auto', position: 'relative', pointerEvents: 'auto' }}>
              {/* Modal header */}
              <div className="pf-preview-header" style={{
                position: 'sticky', top: 0, zIndex: 2, background: 'var(--bg-card)', padding: '16px 24px',
                borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              }}>
                <h3 style={{ margin: 0 }}>Cotización {previewCot.numero}</h3>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button className="btn btn-sm btn-primary" onClick={exportPDF} disabled={exporting}>
                    {exporting ? 'Exportando...' : <>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{verticalAlign:'-2px',marginRight:4}}><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                      Descargar PDF
                    </>}
                  </button>
                  <button className="btn btn-sm btn-secondary" onClick={() => setPreviewCot(null)}>✕ Cerrar</button>
                </div>
              </div>

              {/* PDF preview content */}
              <div ref={previewRef} style={{ background: '#fff', color: 'var(--bg-card)', padding: 40 }}>
                {/* Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 30, borderBottom: '3px solid #1C4587', paddingBottom: 20 }}>
                  <div>
                    <img src="/assets/brand/logo-full-color.svg" alt="1Conecta" style={{ height: 36, marginBottom: 10 }} crossOrigin="anonymous" />
                    {empresa?.razon_social && <div style={{ fontSize: 12, color: '#666' }}>{empresa.razon_social}</div>}
                    {empresa?.ruc && <div style={{ fontSize: 12, color: '#666' }}>RUC: {empresa.ruc}</div>}
                    {empresa?.email && <div style={{ fontSize: 11, color: '#888' }}>{empresa.email}</div>}
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontWeight: 700, fontSize: 22, color: '#1C4587' }}>COTIZACIÓN</div>
                    <div style={{ fontWeight: 700, fontSize: 16, fontFamily: 'monospace' }}>N° {previewCot.numero}</div>
                    {previewCot.fecha_emision && (
                      <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>Fecha: {formatDate(previewCot.fecha_emision)}</div>
                    )}
                    {previewCot.validez_dias > 0 && getExpiryDate(previewCot) && (
                      <div style={{ fontSize: 12, color: '#666' }}>Válida hasta: {formatDate(getExpiryDate(previewCot)!.toISOString().slice(0, 10))}</div>
                    )}
                  </div>
                </div>

                {/* Client info */}
                <div style={{ background: '#f8f9fa', padding: 16, borderRadius: 8, marginBottom: 24 }}>
                  <div style={{ fontWeight: 600, marginBottom: 6, color: '#1C4587' }}>CLIENTE:</div>
                  <div style={{ fontWeight: 700, fontSize: 15 }}>{previewCot.cliente_nombre}</div>
                  {previewCot.cliente_ruc && <div style={{ fontSize: 13 }}>RUC: {previewCot.cliente_ruc}</div>}
                  {previewCot.proyecto_nombre && (
                    <div style={{ fontSize: 13, marginTop: 8, fontStyle: 'italic', color: '#555', borderTop: '1px solid #e0e0e0', paddingTop: 6 }}>
                      Proyecto: {previewCot.proyecto_nombre}
                    </div>
                  )}
                </div>

                {/* Items */}
                {renderPreviewItems(previewCot)}

                {/* Sections */}
                {previewCot.secciones && previewCot.secciones.length > 0 && (
                  <div style={{ marginBottom: 24 }}>
                    {previewCot.secciones.map((sec, idx) => (
                      <div key={idx} style={{ marginBottom: 16 }}>
                        <div style={{ fontWeight: 600, fontSize: 14, color: '#1C4587', marginBottom: 6 }}>{sec.titulo}</div>
                        <div
                          style={{ fontSize: 13, color: '#333', lineHeight: 1.6 }}
                          dangerouslySetInnerHTML={{ __html: sanitizeHtml(processContent(sec.contenido)) }}
                        />
                      </div>
                    ))}
                  </div>
                )}

                {/* Totals */}
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 24 }}>
                  <div style={{ minWidth: 300 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #e0e0e0' }}>
                      <span style={{ color: '#666' }}>Subtotal</span>
                      <span style={{ fontWeight: 600 }}>{formatCurrency(previewCot.subtotal, previewCot.moneda)}</span>
                    </div>
                    {previewCot.descuento_monto > 0 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #e0e0e0' }}>
                        <span style={{ color: '#666' }}>Descuento ({fmtPct(previewCot.descuento_porcentaje)}%)</span>
                        <span style={{ fontWeight: 600, color: '#00b894' }}>−{formatCurrency(previewCot.descuento_monto, previewCot.moneda)}</span>
                      </div>
                    )}
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '6px 0', borderBottom: '1px solid #e0e0e0' }}>
                      <span style={{ color: '#666' }}>IGV ({previewCot.igv_porcentaje}%)</span>
                      <span style={{ fontWeight: 600 }}>{formatCurrency(previewCot.igv_monto, previewCot.moneda)}</span>
                    </div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', fontWeight: 700, fontSize: '1.2rem', borderTop: '2px solid #1C4587', marginTop: 4 }}>
                      <span>TOTAL</span>
                      <span style={{ color: '#1C4587' }}>{formatCurrency(previewCot.total, previewCot.moneda)}</span>
                    </div>
                  </div>
                </div>

                {/* Bank details */}
                {empresa && (empresa.banco || empresa.cuenta_corriente) && (
                  <div style={{ background: '#f8f9fa', padding: 16, borderRadius: 8, marginBottom: 24 }}>
                    <div style={{ fontWeight: 600, marginBottom: 8, color: '#1C4587' }}>DATOS BANCARIOS</div>
                    <table style={{ fontSize: 13 }}><tbody>
                      {empresa.banco && <tr><td style={{ padding: '2px 12px 2px 0', fontWeight: 600 }}>Banco:</td><td>{empresa.banco}</td></tr>}
                      {empresa.cuenta_corriente && <tr><td style={{ padding: '2px 12px 2px 0', fontWeight: 600 }}>Cuenta:</td><td style={{ fontFamily: 'monospace' }}>{empresa.cuenta_corriente}</td></tr>}
                      {empresa.cci && <tr><td style={{ padding: '2px 12px 2px 0', fontWeight: 600 }}>CCI:</td><td style={{ fontFamily: 'monospace' }}>{empresa.cci}</td></tr>}
                    </tbody></table>
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
